/**
 * Typed API client. Sends the session cookie and CSRF token, and turns error
 * envelopes into ApiError (with per-field validation messages).
 */
import type { ApiErrorBody } from "@shared/types.ts";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: Record<string, string> = {},
    readonly requestId: string | null = null,
  ) {
    super(message);
  }
}

let csrfToken = "";
let onUnauthorized: (() => void) | null = null;

export function setCsrfToken(token: string) {
  csrfToken = token;
}

export function setUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler;
}

/** Asks the user to confirm it's them (#43); resolves true when they did. */
let onStepUp: (() => Promise<boolean>) | null = null;

export function setStepUpHandler(handler: (() => Promise<boolean>) | null) {
  onStepUp = handler;
}

export type Params = Record<string, string | number | boolean | null | undefined>;

export function withQuery(path: string, params?: Params): string {
  if (!params) return path;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params))
    if (v !== null && v !== undefined && v !== "") qs.set(k, String(v));
  const s = qs.toString();
  return s ? `${path}${path.includes("?") ? "&" : "?"}${s}` : path;
}

async function request<T>(method: string, path: string, body?: unknown, retried = false): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (method !== "GET") headers["X-CSRF-Token"] = csrfToken;
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(path, { method, headers, body: payload, credentials: "same-origin" });
  } catch {
    throw new ApiError(
      0,
      "network_error",
      "Could not reach the server. Check your connection and try again.",
    );
  }
  if (res.status === 204) return undefined as T;
  const isJson = res.headers.get("content-type")?.includes("application/json");
  const data = isJson ? await res.json() : await res.text();
  if (!res.ok) {
    const err = (data as ApiErrorBody)?.error;
    // Sensitive action: confirm with a second factor, then try once more.
    if (res.status === 403 && err?.code === "step_up_required" && onStepUp && !retried && (await onStepUp()))
      return request<T>(method, path, body, true);
    if (res.status === 401 && onUnauthorized && !path.startsWith("/api/auth/")) onUnauthorized();
    // Server errors carry the request id so support can find the matching log line (#50).
    const requestId = res.headers.get("x-request-id");
    const message = err?.message ?? `Request failed (${res.status})`;
    throw new ApiError(
      res.status,
      err?.code ?? "error",
      res.status >= 500 && requestId ? `${message} (reference ${requestId})` : message,
      err?.fields ?? {},
      requestId,
    );
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, params?: Params) => request<T>("GET", withQuery(path, params)),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  delete: <T = void>(path: string) => request<T>("DELETE", path),
  upload: <T>(path: string, form: FormData) => request<T>("POST", path, form),
};

/** Triggers a browser download of an API export (uses the session cookie). */
export function downloadUrl(path: string, params?: Params): string {
  return withQuery(path, params);
}
